import React, { useState, useEffect } from 'react';
import _, { startCase, isNaN, cloneDeep } from 'lodash';
import { Tabs, Tab } from 'react-bootstrap';

import { faSync } from '@fortawesome/free-solid-svg-icons/faSync';

import { env } from '~/commons/config';

import FormGroup from '~/commons/components/FormGroup.jsx';
import Alert from '~/commons/components/Alert.jsx';
import useHttpStatus from '~/commons/hooks/useHttpStatus';
import { rolesCheck } from '~/commons/helpers';
import { FormConvertBtn } from './orders';
import { FieldErpStatus } from './FieldErpStatus.jsx';
import FormResource from './FormResource.jsx';
import {
  schema, defaults, ListRecords, FieldBasic, FieldSales, FieldDesign, FieldAr, storedFn,
} from './stocks';

const FormStock = ({ design, stock }) => {
  const api = '/api/core/stocks';
  const [res, req] = useHttpStatus();
  const [infoWindow, setInfoWindow] = useState(false);
  useEffect(() => {
    if (res.payload) {
      setInfoWindow(true);
    }
  }, [res]);

  const [resConfig, reqConfig] = useHttpStatus();
  const [resSR, reqSR] = useHttpStatus();
  useEffect(() => {
    reqConfig({
      url: '/api/site/configs',
    });
  }, []);
  useEffect(() => {
    reqSR({ url: '/api/core/users/self/showrooms' });
  }, []);
  const { tradePriceOrgs } = resConfig?.payload ?? {};
  const tpCurrency = _.flatMap(resSR?.payload, (x) => (_.includes(tradePriceOrgs, x.orgId) ? x.currency : []));

  return (
    <FormResource
      {...{
        defaults,
        schema: schema(tpCurrency),
        segment: 'stocks',
        stored: storedFn(stock, design),
        api,
        onSubmit: (submit) => {
          const v = cloneDeep(submit);
          v.design = v.design?._id || v.design?.id || design?._id || null;
          v.showroom = v.showroom || null;

          [
            'yarnContent',
            'style',
            'color',
            'shape',
          ].forEach((key) => {
            v[key] = typeof v[key] === 'string'
              ? v[key].split(',').map((s) => s.trim())
              : v[key];
          });

          return {
            ...v,
            orgId: [
              ...new Set(
                v.orgId?.split(',').reduce((acc, cur) => {
                  const id = parseInt(cur, 10);
                  return isNaN(id) ? [...acc] : [...acc, id];
                }, []),
              ),
            ],
          };
        },
      }}>
      {({ values }) => (
        <>
          {stock && (
            <FormConvertBtn
              res={res}
              icon={faSync}
              disabled={stock?.status === 'OnHold' && rolesCheck('department')}
              onClick={() => {
                req({
                  url: `${api}/manualSync`,
                  method: 'POST',
                  data: {
                    _id: stock._id,
                  },
                });
              }}>
              Sync
            </FormConvertBtn>
          )}
          {infoWindow && (
            <Alert theme="info" onClose={() => setInfoWindow(false)}>
              {JSON.stringify(res.payload)}
            </Alert>
          )}
          <Tabs defaultActiveKey="basic" unmountOnExit>
            <Tab eventKey="basic" title="Basic">
              <FieldBasic />
            </Tab>

            <Tab eventKey="design" title="Design">
              <FieldDesign values={values} />
            </Tab>
            <Tab eventKey="measurement" title="Measurement">
              <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">DIMENSION</h6>
              <div className="row">
                <div className="col">
                  <FormGroup name="sqm" type="number" />
                </div>
                <div className="col">
                  <FormGroup name="size" />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <FormGroup name="width" type="number" />
                </div>
                <div className="col">
                  <FormGroup name="length" type="number" />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <FormGroup name="quantity" type="number" />
                </div>
                <div className="col">
                  <FormGroup name="quantityUnit" as="select">
                    {env?.unitOfMeasure.map((v) => (
                      <option value={v} key={v}>
                        {startCase(v)}
                      </option>
                    ))}
                  </FormGroup>
                </div>
              </div>
            </Tab>

            <Tab eventKey="sales" title="Sales">
              <FieldSales values={values} tpCurrency={tpCurrency} />
            </Tab>
            <Tab eventKey="art" title="Artwork">
              <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">ARTWORK</h6>
              <FormGroup
                name="artworks"
                as="objSlider"
                api="/api/core/stocks/files"
                label="Artwork"
                fileOpts={{
                  accept: 'application/pdf, image/jpeg, image/png',
                }}
                gridClassName="col-3 my-3"
                slideClassName="file-slide-contain square"
                childKey="url"
                defaultChild={(f) => ({
                  url: f,
                  title: f.name,
                })}>
                {({ i }) => <FormGroup name={`artworks.${i}.title`} label={`File ${i + 1}`} />}
              </FormGroup>
            </Tab>
            <Tab eventKey="records" title="OnHold Record">
              <ListRecords _id={stock?._id} />
            </Tab>
            <Tab eventKey="erpStats" title="ERP Status">
              <FieldErpStatus />
            </Tab>
            <Tab eventKey="ar" title="AR">
              <FieldAr values={values} />
            </Tab>
          </Tabs>
        </>
      )}
    </FormResource>
  );
};

export default FormStock;
